









































































import { Vue, Prop, Component } from 'vue-property-decorator';
import { FileUpload } from '@/models/FileUpload';
import http from '@/services/Http';

import { websiteText } from '@/mixins/websiteTexts';

@Component
export default class FilesUploader extends Vue {
    @Prop() label!: string;
    @Prop({ default: false }) formError!: boolean;
    @Prop({ default: undefined }) acceptedFiles?: string;

    uploadFieldName: string = 'file';
    uploadLoading: boolean = false;
    isSelectingFile: boolean = false;
    uploadedFiles: FileUpload[] = [];

    fileTypeError: string | null = null;
    invalidFileNames: string[] = [];
    fileCount: number = 0; 
    isDragging: boolean = false;

    private resizeObserver?: ResizeObserver;

    constructor() {
        super();
        this.uploadedFiles = [];
    }

    mounted() {
        this.updateFileUploadItemMaxWidth();

        if (typeof ResizeObserver !== 'undefined') {
            this.resizeObserver = new ResizeObserver(() => this.updateFileUploadItemMaxWidth());
            this.resizeObserver.observe(this.$el as Element);
        } else {
            window.addEventListener('resize', this.updateFileUploadItemMaxWidth);
        }
    }

    beforeDestroy() {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }

        window.removeEventListener('resize', this.updateFileUploadItemMaxWidth);
    }

    private updateFileUploadItemMaxWidth() {
        const uploadElement = this.$refs.fileUpload as HTMLDivElement | undefined;

        if (!uploadElement) {
            return;
        }

        const styles = window.getComputedStyle(uploadElement);
        // This TS toolchain's lib.dom.d.ts types these as `string | null` (fixed in later
        // TypeScript versions) even though getComputedStyle never actually returns null here.
        const paddingLeft = parseFloat(styles.paddingLeft || '0') || 0;
        const paddingRight = parseFloat(styles.paddingRight || '0') || 0;
        const borderLeft = parseFloat(styles.borderLeftWidth || '0') || 0;
        const borderRight = parseFloat(styles.borderRightWidth || '0') || 0;
        const deleteButtonWidth = 16 + 8;
        const maxWidth = Math.max(0, uploadElement.clientWidth - paddingLeft - paddingRight - borderLeft - borderRight - deleteButtonWidth);

        uploadElement.style.setProperty('--file-upload-item-max-width', `${maxWidth}px`);
    }

    protected onDrop(e: DragEvent) {
        this.isDragging = false;
        
        if (e.dataTransfer && e.dataTransfer.files.length > 0) {
            this.filesChange(this.uploadFieldName, e.dataTransfer.files);
        }
    }

    protected filesChange(fieldName: string, fileList: FileList) {
        this.fileTypeError = null; 
        const formData = new FormData();

        if (!fileList || !fileList.length) {
            return;
        }

        const validFiles: File[] = [];
        const invalidFiles: File[] = [];

        if (this.acceptedFiles) {
            const acceptedTypes = this.acceptedFiles.split(',').map(t => t.trim().toLowerCase());
            
            for (let i = 0; i < fileList.length; i++) {
                const file = fileList[i];
                
                const isValid = acceptedTypes.some(type => {
                    if (type.endsWith('/*')) {
                        return file.type.startsWith(type.replace('/*', '/'));
                    } else if (type.startsWith('.')) {
                        return file.name.toLowerCase().endsWith(type);
                    } else {
                        return file.type === type;
                    }
                });

                if (isValid) {
                    validFiles.push(file);
                } else {
                    invalidFiles.push(file);
                }
            }
        } else {
            for (let i = 0; i < fileList.length; i++) {
                validFiles.push(fileList[i]);
            }
        }

        if (invalidFiles.length > 0) {
            this.invalidFileNames = invalidFiles.map(f => f.name);
            this.fileTypeError = websiteText('site.contactform.wrongFileFormat');
            this.$emit('clearError');
        }

        if (validFiles.length > 0) {
            this.fileCount = validFiles.length;
            
            validFiles.forEach(item => {
                formData.append(fieldName, item);
            });
            
            this.save(formData);
        } else {
            this.uploadLoading = false; 
        }
    }


    private save(formData: FormData) {
        this.uploadLoading = true;
        http.post(`/api/fileupload/`, formData, true)
            .then((res: Response) => res.json())
            .then((result: FileUpload[]) => {
                result.forEach(fu => this.uploadedFiles.push(fu));
            })
            .catch((e: any) => {
                console.error('File upload failed', e);
            })
            .finally(() => {
                this.uploadLoading = false;
                this.$emit('input', this.uploadedFiles);
                this.$emit('clearError');
            });
    }

    protected remove(fileSystemId: string) {
        this.uploadedFiles = this.uploadedFiles.filter(f => f.fileSystemId !== fileSystemId);
        this.$emit('input', this.uploadedFiles);
        if (this.uploadedFiles.length === 0) {
            this.$emit('blur'); 
        } else {
            this.$emit('clearError'); 
        }
    }

    protected onInputClick() {
        this.isSelectingFile = true;
        window.addEventListener('focus', this.onWindowFocus, { once: true });
    }

    protected onInputBlur() {
        if (!this.isSelectingFile) {
            this.$emit('blur');
        }
    }

    protected onWindowFocus() {
        setTimeout(() => {
            this.isSelectingFile = false;
            
            if (this.uploadedFiles.length === 0) {
                this.$emit('blur');
            }
        }, 300);
    }

    protected onUploadAreaClick(e: MouseEvent) {
        const target = e.target as HTMLElement;

        // Don't open file dialog when clicking interactive parts (file list, delete buttons, or buttons)
        if (
            target.closest('.file-upload__files') ||
            target.closest('.file-upload__delete') ||
            target.closest('button') ||
            target.tagName === 'INPUT'
        ) {
            return;
        }

        this.openFileDialog();
    }

    protected openFileDialog() {
        const input = (this.$refs.fileInput as HTMLInputElement | undefined);
        if (input) {
            input.click();
            this.onInputClick();
        }
    }
}
